import { useEffect, useState } from "react";
import { useApiContext } from 'context/ApiContext';

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDDatePicker from "components/MDDatePicker";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import TurbineDashboardLayout from "examples/LayoutContainers/DashboardLayout";
import TurbineDashboardNavbar from "examples/Navbars/DashboardNavbar";

import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
} from 'recharts';

function ParkStatistics() {
    const { alertsNumPaS, APIRequestType, fromDatePaS, getAPI, parkActHistogramPaS, parkAlertsHistogramPaS, parkPaS, setAlertsNumPaS, setFromDatePaS, 
        setParkActHistogramPaS, setParkAlertsHistogramPaS, setParkPaS, setToDatePaS, toDatePaS } = useApiContext();
    const [parkValues, setParkValues] = useState([]);

    useEffect(() => {
        getAPI(APIRequestType.GET_PARK);
        getAPI(APIRequestType.GET_PARK_ALERTS);
        getAPI(APIRequestType.GET_PARK_ALERTS_HISTOGRAM);
        getAPI(APIRequestType.GET_PARK_ACT_HISTOGRAM);
    }, [fromDatePaS, toDatePaS]);

    useEffect(() => {
        if (parkPaS && parkPaS.park) {
            setParkValues(parkPaS.park);
        }
    }, [parkPaS]);

    useEffect(() => {
        return () => {
            resetPaS();
        };
    }, []);

    const resetPaS = () => {
        setParkPaS(null);
        setAlertsNumPaS(0);
        setParkActHistogramPaS([]);
        setParkAlertsHistogramPaS([]);
    };

    const handleFromDateChange = (date) => {
        setFromDatePaS(date || null);
        if (date && toDatePaS && date > toDatePaS) {
            setToDatePaS(date);
        }
    };

    const handleToDateChange = (date) => {
        setToDatePaS(date || null);
    };

    // Function to handle the CSV download
    const handleDownloadCSV = () => {
        getAPI(APIRequestType.DOWNLOAD_PARK_CSV);
    };

    const actHistogramData = parkActHistogramPaS || [];
    const alertHistogramData = parkAlertsHistogramPaS || [];

    // Adapter le radius des barres de l'histogramme en fonction du nombre de valeurs en abscisse
    function calculateRadius(numBars) {
        if (numBars <= 20) return [10, 10, 0, 0];  // radius max
        if (numBars >= 100) return [2, 2, 0, 0];   // radius min
        // interpolation linéaire entre 10 et 2
        const radius = 10 - ((numBars - 20) * (8 / 80));
        return [radius, radius, 0, 0];
    }

    const alertRadius = calculateRadius(alertHistogramData.length);
    const actRadius = calculateRadius(actHistogramData.length);

    return (
        <TurbineDashboardLayout>
            <TurbineDashboardNavbar />
            <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={3} mt={1} mb={0} ml={-0.5}>
                <MDBox display="flex" alignItems="center" gap={0}>
                    {/* Date Picker "From" */}
                    <MDDatePicker
                        label="From"
                        value={fromDatePaS}
                        onChange={handleFromDateChange}
                        scale={0.9}
                    />

                    {/* Date Picker "To" */}
                    <MDDatePicker
                        label="To"
                        value={toDatePaS}
                        onChange={handleToDateChange}
                        minDate={fromDatePaS} // Empêche de choisir une date antérieure
                        scale={0.9}
                    />
                </MDBox>
            </MDBox>

            <MDBox position="relative" mb={3} mt={2}>
                <Card
                    sx={{
                        left: "0",  // Aligne le Card à gauche de l'image
                        right: "0", // Aligne le Card à droite de l'image
                        py: 0,
                        px: 5,
                    }}
                >
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2} mt={2}>
                        <MDBox height="100%" lineHeight={1}>
                            <MDTypography fontSize="17px" fontWeight="small">
                                {alertsNumPaS === 0
                                    ?
                                    <>
                                        No statistics available for <strong>{parkValues.name}</strong>.
                                    </>
                                    : (
                                        <>
                                            Download statistics for <strong>{parkValues.name}</strong> as a CSV file.
                                        </>
                                    )
                                }
                            </MDTypography>
                        </MDBox>
                        <MDBox display="flex" justifyContent="flex-end" flexGrow={1}>
                            <MDButton
                                disabled={alertsNumPaS === 0}
                                sx={{
                                    '&:hover': {
                                        backgroundColor: "#b7e4fd", // Couleur au survol
                                    },
                                }}
                            >
                                <MDBox
                                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                >
                                    <MDTypography
                                        variant="h6"
                                        color="dark"
                                        fontWeight="medium"
                                        sx={{ mt: 0.2 }}
                                        onClick={() => handleDownloadCSV()}
                                    >
                                        DOWNLOAD CSV
                                    </MDTypography>
                                    <Icon color="text" fontSize="30px" sx={{ mt: 0.2 }}> download </Icon>
                                </MDBox>
                            </MDButton>
                        </MDBox>
                    </MDBox>
                </Card>
            </MDBox>
            <MDBox mt={3} mb={5}>
                <Grid container spacing={3}>
                    {/* Histogram section */}
                    {alertHistogramData.length > 0 && (
                        <Grid item xs={12} md={alertHistogramData.length > 100 ? 12 : 6}>
                            <Card sx={{ height: "100%", pt: 2, pb: 1 }}>
                                <MDTypography variant="h6" gutterBottom textAlign="center">
                                    ALERT Histogram
                                </MDTypography>
                                <MDBox display="flex" justifyContent="center" sx={{ ml: -5 }}>
                                    <ResponsiveContainer width="95%" height={200}>
                                        <BarChart data={alertHistogramData}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis
                                                dataKey="day"
                                                tick={{ fontSize: 12 }}
                                            />
                                            <YAxis
                                                allowDecimals={false}
                                                tick={{ fontSize: 12 }}
                                            />
                                            <Tooltip
                                                contentStyle={{
                                                    backgroundColor: "#fff",
                                                    borderRadius: "8px",
                                                    border: "0px",
                                                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                                                    padding: "8px",
                                                }}
                                                labelStyle={{
                                                    color: "#344767",
                                                    fontWeight: "bold",
                                                    fontSize: "14px",
                                                    marginBottom: "-4px",
                                                }}
                                                itemStyle={{
                                                    color: "#555",
                                                    fontSize: "13px",
                                                    textAlign: "center",
                                                    marginBottom: "-4px",
                                                }}
                                                formatter={(value, name) => [value, 'Alert Number']}
                                            />
                                            <Bar
                                                dataKey="count"
                                                fill="#1097e1"
                                                radius={alertRadius}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </MDBox>
                            </Card>
                        </Grid>
                    )}
                    {actHistogramData.length > 0 && (
                        <Grid item xs={12} md={alertHistogramData.length > 100 ? 12 : 6}>
                            <Card sx={{ height: "100%", pt: 2, pb: 1 }}>
                                <MDTypography variant="h6" gutterBottom textAlign="center">
                                    ACTIVITY Histogram
                                </MDTypography>
                                <MDBox display="flex" justifyContent="center" sx={{ ml: -5 }}>
                                    <ResponsiveContainer width="95%" height={200}>
                                        <BarChart data={actHistogramData}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis
                                                dataKey="type"
                                                tick={{ fontSize: 12 }}
                                            />
                                            <YAxis
                                                allowDecimals={false}
                                                tick={{ fontSize: 12 }}
                                            />
                                            <Tooltip
                                                contentStyle={{
                                                    backgroundColor: "#fff",
                                                    borderRadius: "8px",
                                                    border: "0px",
                                                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                                                    padding: "8px",
                                                }}
                                                labelStyle={{
                                                    color: "#344767",
                                                    fontWeight: "bold",
                                                    fontSize: "14px",
                                                    marginBottom: "-4px",
                                                }}
                                                itemStyle={{
                                                    color: "#555",
                                                    fontSize: "13px",
                                                    textAlign: "center",
                                                    marginBottom: "-4px",
                                                }}
                                                formatter={(value, name) => [value, 'Alert Number']}
                                            />
                                            <Bar
                                                dataKey="count"
                                                fill="#1097e1"
                                                radius={actRadius}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </MDBox>
                            </Card>
                        </Grid>
                    )}
                </Grid>
            </MDBox>
            {alertHistogramData.length === 0 && actHistogramData.length === 0 && (
                <MDBox display="flex" alignItems="center" justifyContent="center" height="20vh">
                    <MDTypography
                        variant="button"
                        fontWeight="bold"
                        color="secondary"
                        opacity="40%"
                        ml={1}
                        fontSize="5rem"
                    >
                        NO ALERTS
                    </MDTypography>
                </MDBox>
            )}
        </TurbineDashboardLayout >
    );
}

export default ParkStatistics;


